import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2, Wallet, FileText, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SyrianFlag from "@/components/SyrianFlag";
import { CURRENCY_CATALOG, currencyName, currencyFlag } from "@/lib/currencies";

const CURRENCY_META = {
  SYP: { name: "ليرة سورية", color: "from-emerald-500 to-emerald-700", flag: "SY" },
  TRY: { name: "ليرة تركية", color: "from-red-500 to-red-700", flag: "🇹🇷" },
  USD: { name: "دولار أمريكي", color: "from-green-600 to-green-800", flag: "🇺🇸" },
  EUR: { name: "يورو", color: "from-blue-500 to-blue-700", flag: "🇪🇺" },
};
const GRADIENTS = ["from-emerald-500 to-emerald-700", "from-red-500 to-red-700", "from-green-600 to-green-800", "from-blue-500 to-blue-700", "from-purple-500 to-purple-700", "from-amber-500 to-amber-700", "from-cyan-500 to-cyan-700", "from-pink-500 to-pink-700"];
const meta = (cur) => CURRENCY_META[cur] || {
  name: currencyName(cur),
  color: GRADIENTS[(cur || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % GRADIENTS.length],
  flag: currencyFlag(cur),
};

const FlagEl = ({ code, flag }) =>
  code === "SYP" ? <SyrianFlag width={16} height={11} /> : <span>{flag}</span>;

export default function CashBoxes() {
  const { currentCompany } = useAuth();
  const [boxes, setBoxes] = useState([]);
  const [txs, setTxs] = useState([]);
  const [filterCurrency, setFilterCurrency] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ currency: "SYP", kind: "deposit", amount: 0, description: "", reference: "" });

  const [addBoxOpen, setAddBoxOpen] = useState(false);
  const [boxForm, setBoxForm] = useState({ name: "", currency: "SYP" });
  const [editingBox, setEditingBox] = useState(null);

  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtBox, setStmtBox] = useState(null);
  const [stmtDate, setStmtDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [stmt, setStmt] = useState(null);

  // Transfer dialog state
  const [xferOpen, setXferOpen] = useState(false);
  const [xfer, setXfer] = useState({ from_box_id: "", to_box_id: "", amount: "", notes: "" });
  const [xferBusy, setXferBusy] = useState(false);

  const openTransfer = () => {
    setXfer({ from_box_id: "", to_box_id: "", amount: "", notes: "" });
    setXferOpen(true);
  };
  const doTransfer = async () => {
    if (!xfer.from_box_id || !xfer.to_box_id) return toast.error("اختر الصندوق المُصدِر والمُستقبل");
    if (xfer.from_box_id === xfer.to_box_id) return toast.error("الصندوقان متطابقان");
    if (!xfer.amount || +xfer.amount <= 0) return toast.error("المبلغ يجب أن يكون أكبر من صفر");
    const fromBox = boxes.find(b => b.id === xfer.from_box_id);
    const toBox = boxes.find(b => b.id === xfer.to_box_id);
    if (fromBox && toBox && fromBox.currency !== toBox.currency) {
      return toast.error("العملات مختلفة — لا يمكن التحويل بين عملتين مختلفتين مباشرة");
    }
    setXferBusy(true);
    try {
      const { data } = await api.post("/cashboxes/transfer", { from_box_id: xfer.from_box_id, to_box_id: xfer.to_box_id, amount: +xfer.amount, notes: xfer.notes || "تحويل يدوي" });
      toast.success(`تم تحويل ${data.amount?.toLocaleString?.("ar-EG") || xfer.amount} ${data.currency || fromBox?.currency}`);
      setXferOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setXferBusy(false); }
  };

  const load = async () => {
    try {
      const [b, t] = await Promise.all([
        api.get("/cashboxes"),
        api.get("/cashboxes/transactions", { params: filterCurrency ? { currency: filterCurrency } : {} }),
      ]);
      setBoxes(b.data); setTxs(t.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [currentCompany?.id, filterCurrency]); // eslint-disable-line

  const openFor = (currency, kind) => {
    setForm({ currency, kind, amount: 0, description: "", reference: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.amount || +form.amount <= 0) return toast.error("المبلغ يجب أن يكون أكبر من صفر");
    try {
      await api.post("/cashboxes/transactions", { ...form, amount: +form.amount });
      toast.success("تم تسجيل الحركة"); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("حذف الحركة؟ سيتم تصحيح رصيد الصندوق تلقائياً.")) return;
    try { await api.delete(`/cashboxes/transactions/${id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const saveBox = async () => {
    if (!boxForm.name.trim()) return toast.error("أدخل اسم الصندوق");
    try {
      if (editingBox) await api.put(`/cashboxes/${editingBox.id}`, boxForm);
      else await api.post("/cashboxes", boxForm);
      toast.success("تم الحفظ"); setAddBoxOpen(false); setEditingBox(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const delBox = async (b) => {
    if (!confirm(`حذف الصندوق «${b.name || b.currency}»؟ يجب أن يكون رصيده صفراً وبدون حركات.`)) return;
    try { await api.delete(`/cashboxes/${b.id}`); toast.success("تم حذف الصندوق"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openStmt = async (box) => {
    setStmtBox(box); setStmtOpen(true); setStmt(null);
    try {
      const { data } = await api.get(`/cashboxes/${box.id}/statement`, { params: { date: stmtDate } });
      setStmt(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const reloadStmt = async () => {
    if (!stmtBox) return;
    try {
      const { data } = await api.get(`/cashboxes/${stmtBox.id}/statement`, { params: { date: stmtDate } });
      setStmt(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { if (stmtOpen && stmtBox) reloadStmt(); }, [stmtDate]); // eslint-disable-line

  const printStmt = () => {
    if (!stmt) return;
    const rows = stmt.items.map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${new Date(it.created_at).toLocaleTimeString("ar-EG")}</td>
        <td>${it.description || ""}</td>
        <td>${it.invoice_number ? "#" + it.invoice_number : "—"}</td>
        <td style="color:${it.amount > 0 ? "#059669" : "#dc2626"};font-weight:bold">${it.amount > 0 ? "+" : ""}${Number(it.amount).toLocaleString("ar-EG")}</td>
        <td style="font-weight:bold">${Number(it.running_balance).toLocaleString("ar-EG")}</td>
      </tr>`).join("");
    const html = `<!doctype html><html dir="rtl"><head><meta charset="utf-8"><title>كشف صندوق ${stmt.box.name} — ${stmt.date}</title>
      <style>
        @page { size: A4; margin: 12mm; }
        body { font-family: Arial, "Segoe UI", sans-serif; color: #1e293b; }
        h1 { font-size: 20px; border-bottom: 3px solid #2563eb; padding-bottom: 6px; }
        .meta { display:flex; justify-content:space-between; margin: 10px 0; font-size: 13px; color:#475569 }
        .summary { display:grid; grid-template-columns: repeat(4, 1fr); gap:10px; margin: 14px 0 20px }
        .kpi { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; text-align:center }
        .kpi .lbl { font-size: 11px; color:#64748b }
        .kpi .val { font-size: 18px; font-weight:bold; font-variant-numeric: tabular-nums; margin-top: 4px }
        table { width:100%; border-collapse: collapse; font-size: 12px }
        th { background: #f1f5f9; text-align:right; padding: 6px; font-weight:bold; color:#334155 }
        td { padding: 6px; border-bottom: 1px solid #e5e7eb; text-align:right; font-variant-numeric: tabular-nums }
        .footer { margin-top: 20px; text-align:center; color:#94a3b8; font-size:10px }
        @media print { .noprint { display:none } }
      </style></head><body>
      <h1>كشف حساب صندوق — ${stmt.box.name}</h1>
      <div class="meta">
        <div>${stmt.company_name}</div>
        <div>عن يوم: <strong>${stmt.date}</strong></div>
        <div>العملة: <strong>${stmt.box.currency}</strong></div>
      </div>
      <div class="summary">
        <div class="kpi"><div class="lbl">رصيد افتتاحي</div><div class="val">${Number(stmt.opening).toLocaleString("ar-EG")}</div></div>
        <div class="kpi"><div class="lbl">وارد اليوم</div><div class="val" style="color:#059669">+${Number(stmt.in_total).toLocaleString("ar-EG")}</div></div>
        <div class="kpi"><div class="lbl">صادر اليوم</div><div class="val" style="color:#dc2626">-${Number(stmt.out_total).toLocaleString("ar-EG")}</div></div>
        <div class="kpi" style="background:#fef3c7"><div class="lbl">رصيد ختامي</div><div class="val">${Number(stmt.closing).toLocaleString("ar-EG")} ${stmt.box.currency}</div></div>
      </div>
      <table><thead><tr>
        <th>#</th><th>الوقت</th><th>البيان</th><th>مرجع</th><th>المبلغ</th><th>الرصيد الجاري</th>
      </tr></thead><tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#94a3b8;padding:20px">لا توجد حركات في هذا اليوم</td></tr>'}</tbody></table>
      <div class="footer">Muhasabaty — تم توليد الكشف: ${new Date().toLocaleString("ar-EG")}</div>
      </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const idoc = iframe.contentWindow.document;
    idoc.open(); idoc.write(html); idoc.close();
    setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} setTimeout(() => iframe.remove(), 1500); }, 350);
  };

  const boxesByCur = useMemo(() => {
    const g = {};
    for (const b of boxes) { (g[b.currency] = g[b.currency] || []).push(b); }
    return g;
  }, [boxes]);

  return (
    <div className="space-y-6" data-testid="cashboxes-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">صناديق النقدية</h1>
          <p className="text-sm text-muted-foreground mt-1">صناديق متعددة لكل عملة — إيداع/سحب مع كشف يومي مطبوع</p>
        </div>
        <Button onClick={() => { setEditingBox(null); setBoxForm({ name: "", currency: "SYP" }); setAddBoxOpen(true); }} className="gap-1" data-testid="new-box-btn">
          <Plus className="w-4 h-4" /> صندوق جديد
        </Button>
        <Button variant="outline" onClick={openTransfer} className="gap-1 border-blue-300 text-blue-700" data-testid="transfer-btn"><ArrowUp className="w-4 h-4" /> تحويل</Button>
        <Button variant="outline" onClick={async () => {
          if (!confirm("مزامنة الفواتير القديمة مع الصناديق؟ (يستثني ما تم تسجيله مسبقاً)")) return;
          try {
            const { data } = await api.post("/cashboxes/sync-invoices");
            toast.success(`تمت المزامنة: ${data.created} حركة جديدة من أصل ${data.scanned} فاتورة`);
            load();
          } catch (e) { toast.error(formatErr(e)); }
        }} className="gap-1 border-emerald-300 text-emerald-700" data-testid="sync-btn"><ArrowDown className="w-4 h-4" /> مزامنة رجعية</Button>
        <Button variant="outline" onClick={async () => {
          const emptyExtra = boxes.filter(b => !["SYP","USD"].includes(b.currency) && Math.abs(b.balance || 0) < 0.001);
          if (!emptyExtra.length) { toast.info("لا توجد صناديق فارغة غير افتراضية لحذفها"); return; }
          if (!confirm(`سيتم حذف الصناديق الفارغة غير المستخدمة (بدون رصيد وبدون حركات)، مع الإبقاء على صندوقي الليرة والدولار. متابعة؟`)) return;
          try {
            const { data } = await api.post("/cashboxes/cleanup-unused");
            toast.success(data.deleted ? `تم حذف ${data.deleted} صندوق غير مستخدم` : "لا يوجد صناديق قابلة للحذف (قد تحتوي على حركات)");
            load();
          } catch (e) { toast.error(formatErr(e)); }
        }} className="gap-1 border-amber-300 text-amber-700" data-testid="cleanup-boxes-btn"><Trash2 className="w-4 h-4" /> تنظيف الصناديق الفارغة</Button>
      </div>

      {Object.keys(boxesByCur).sort().map(cur => {
        const list = boxesByCur[cur] || [];
        if (!list.length) return null;
        const m = meta(cur);
        return (
          <div key={cur} className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-bold text-muted-foreground">
              <FlagEl code={cur} flag={m.flag} /> {m.name} <span className="text-xs opacity-60">({cur})</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{list.length} صندوق</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {list.map(b => (
                <Card key={b.id} className="p-4 border-border overflow-hidden relative" data-testid={`box-${b.id}`}>
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${m.color}`} />
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{b.name || `صندوق ${cur}`}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{cur}</div>
                    </div>
                    <Wallet className="w-4 h-4 text-primary" />
                  </div>
                  <div className={`mt-3 text-xl font-heading font-bold tabular-nums ${b.balance < 0 ? "text-destructive" : ""}`} data-testid={`balance-${b.id}`}>
                    {fmtMoney(b.balance, cur)}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-1.5">
                    <Button size="sm" variant="outline" onClick={()=>openFor(cur, "deposit")} data-testid={`deposit-${b.id}`} className="text-emerald-700 h-8 text-xs">
                      <ArrowDown className="w-3 h-3 me-1" /> إيداع
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>openFor(cur, "withdraw")} data-testid={`withdraw-${b.id}`} className="text-destructive h-8 text-xs">
                      <ArrowUp className="w-3 h-3 me-1" /> سحب
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>openStmt(b)} className="text-blue-600 h-8 text-xs col-span-2 gap-1" data-testid={`stmt-${b.id}`}>
                      <FileText className="w-3 h-3" /> كشف يومي
                    </Button>
                  </div>
                  {b.name && (
                    <div className="mt-2 flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={()=>{setEditingBox(b); setBoxForm({ name: b.name || "", currency: b.currency }); setAddBoxOpen(true);}} className="w-6 h-6" data-testid={`edit-box-${b.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button size="icon" variant="ghost" onClick={()=>delBox(b)} className="w-6 h-6 text-destructive" data-testid={`del-box-${b.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between flex-wrap gap-3 pt-4">
        <h2 className="text-lg font-heading font-bold">سجل الحركات</h2>
        <div className="flex items-center gap-2">
          <Label className="text-xs">تصفية بالعملة</Label>
          <Select value={filterCurrency || "all"} onValueChange={(v)=>setFilterCurrency(v==="all"?"":v)}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">الكل</SelectItem>
              {Object.keys(boxesByCur).sort().map(cur => <SelectItem key={cur} value={cur}>{cur}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">التاريخ</TableHead>
            <TableHead className="text-start">النوع</TableHead>
            <TableHead className="text-start">العملة</TableHead>
            <TableHead className="text-start">المبلغ</TableHead>
            <TableHead className="text-start">البيان</TableHead>
            <TableHead className="text-start">بواسطة</TableHead>
            <TableHead className="text-start"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {txs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">لا توجد حركات</TableCell></TableRow>}
            {txs.map(t => (
              <TableRow key={t.id}>
                <TableCell className="text-xs">{new Date(t.created_at).toLocaleString("ar-EG")}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded ${t.amount>0 ? "bg-emerald-50 text-emerald-700" : "bg-destructive/10 text-destructive"}`}>
                    {t.kind === "deposit" ? "إيداع" : t.kind === "withdraw" ? "سحب" : (t.auto ? "تلقائي" : "تحويل")}
                  </span>
                </TableCell>
                <TableCell className="font-mono">{t.currency}</TableCell>
                <TableCell className={`tabular-nums font-bold ${t.amount>0 ? "text-emerald-700" : "text-destructive"}`}>
                  {t.amount>0 ? "+" : ""}{fmtMoney(t.amount, t.currency)}
                </TableCell>
                <TableCell>{t.description || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{t.created_by_name || "—"}</TableCell>
                <TableCell>{!t.auto && <Button size="sm" variant="ghost" onClick={()=>del(t.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Deposit / Withdraw dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cashbox-tx-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {form.kind === "deposit" ? <><ArrowDown className="w-5 h-5 text-emerald-600" /> إيداع نقدي</> : <><ArrowUp className="w-5 h-5 text-destructive" /> سحب نقدي</>}
              <span className="text-muted-foreground text-sm font-normal">— {CURRENCY_META[form.currency]?.name} ({form.currency})</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>المبلغ</Label><Input type="number" step="0.01" min="0" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} data-testid="tx-amount-input" autoFocus /></div>
            <div><Label>البيان</Label><Input value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} placeholder="سبب الحركة..." data-testid="tx-description-input" /></div>
            <div><Label>مرجع (اختياري)</Label><Input value={form.reference} onChange={(e)=>setForm({...form, reference: e.target.value})} placeholder="رقم فاتورة/إيصال..." /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-tx-button">حفظ الحركة</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / rename cash box dialog */}
      <Dialog open={addBoxOpen} onOpenChange={setAddBoxOpen}>
        <DialogContent data-testid="add-box-dialog">
          <DialogHeader><DialogTitle>{editingBox ? "تعديل اسم الصندوق" : "إضافة صندوق جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>اسم الصندوق</Label><Input value={boxForm.name} onChange={(e)=>setBoxForm({...boxForm, name: e.target.value})} placeholder="مثال: كاش الفرع 2 / صندوق المدير" data-testid="box-name-input" autoFocus /></div>
            {!editingBox && (
              <div>
                <Label>العملة</Label>
                <Select value={boxForm.currency} onValueChange={(v)=>setBoxForm({...boxForm, currency: v})}>
                  <SelectTrigger data-testid="box-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {CURRENCY_CATALOG.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={saveBox} data-testid="save-box-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash box daily statement dialog */}
      <Dialog open={stmtOpen} onOpenChange={(o)=>{setStmtOpen(o); if(!o){setStmt(null); setStmtBox(null);}}}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="stmt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <FileText className="w-5 h-5 text-blue-600" />
              كشف صندوق <span className="text-primary">{stmtBox?.name || stmtBox?.currency}</span>
              <Input type="date" value={stmtDate} onChange={(e)=>setStmtDate(e.target.value)} className="w-40 h-8 text-xs ms-2" data-testid="stmt-date-input" />
              <Button size="sm" variant="outline" onClick={printStmt} disabled={!stmt} className="ms-auto gap-1" data-testid="print-stmt-btn">
                <FileText className="w-3.5 h-3.5" /> طباعة PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          {!stmt ? (
            <div className="py-12 text-center text-muted-foreground text-sm">جاري التحميل…</div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <Card className="p-3"><div className="text-[10px] text-muted-foreground">افتتاحي</div><div className="text-base font-bold tabular-nums">{fmtMoney(stmt.opening, stmt.box.currency)}</div></Card>
                <Card className="p-3 bg-emerald-50 dark:bg-emerald-950/20"><div className="text-[10px] text-emerald-700">وارد</div><div className="text-base font-bold tabular-nums text-emerald-700">+{fmtMoney(stmt.in_total, stmt.box.currency)}</div></Card>
                <Card className="p-3 bg-red-50 dark:bg-red-950/20"><div className="text-[10px] text-destructive">صادر</div><div className="text-base font-bold tabular-nums text-destructive">-{fmtMoney(stmt.out_total, stmt.box.currency)}</div></Card>
                <Card className="p-3 bg-amber-50 dark:bg-amber-950/20 ring-1 ring-amber-300"><div className="text-[10px] text-amber-800">ختامي</div><div className="text-base font-bold tabular-nums text-amber-800">{fmtMoney(stmt.closing, stmt.box.currency)}</div></Card>
              </div>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>الوقت</TableHead>
                    <TableHead>البيان</TableHead>
                    <TableHead>مرجع</TableHead>
                    <TableHead className="text-end">المبلغ</TableHead>
                    <TableHead className="text-end">الرصيد الجاري</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {stmt.items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا توجد حركات في هذا اليوم</TableCell></TableRow>}
                    {stmt.items.map((it, i) => (
                      <TableRow key={it.id} data-testid={`stmt-row-${i}`}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell className="text-xs">{new Date(it.created_at).toLocaleTimeString("ar-EG")}</TableCell>
                        <TableCell className="text-xs truncate max-w-[220px]">{it.description || "—"}{it.auto && <span className="ms-1 text-[9px] px-1 py-0.5 rounded bg-blue-50 text-blue-700">تلقائي</span>}</TableCell>
                        <TableCell className="text-xs font-mono">{it.invoice_number ? `#${it.invoice_number}` : "—"}</TableCell>
                        <TableCell className={`text-end tabular-nums font-bold ${it.amount>0?"text-emerald-700":"text-destructive"}`}>
                          {it.amount>0?"+":""}{fmtMoney(it.amount, it.currency)}
                        </TableCell>
                        <TableCell className="text-end tabular-nums font-bold">{fmtMoney(it.running_balance, it.currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Transfer dialog */}
      <Dialog open={xferOpen} onOpenChange={setXferOpen}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <ArrowUp className="w-5 h-5" /> تحويل بين الصناديق
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>من صندوق</Label>
              <Select value={xfer.from_box_id || undefined} onValueChange={(v)=>setXfer(x=>({...x, from_box_id: v}))}>
                <SelectTrigger data-testid="xfer-from-select"><SelectValue placeholder="اختر الصندوق المُصدِر" /></SelectTrigger>
                <SelectContent>
                  {boxes.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name || `صندوق ${b.currency}`} · {b.currency} · رصيد {b.balance?.toLocaleString?.("ar-EG") || 0}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>إلى صندوق</Label>
              <Select value={xfer.to_box_id || undefined} onValueChange={(v)=>setXfer(x=>({...x, to_box_id: v}))}>
                <SelectTrigger data-testid="xfer-to-select"><SelectValue placeholder="اختر الصندوق المُستقبل" /></SelectTrigger>
                <SelectContent>
                  {boxes.filter(b => b.id !== xfer.from_box_id).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name || `صندوق ${b.currency}`} · {b.currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>المبلغ</Label>
              <Input type="number" step="0.01" min="0" value={xfer.amount} onChange={(e)=>setXfer(x=>({...x, amount: e.target.value}))} data-testid="xfer-amount-input" autoFocus placeholder="0" />
            </div>
            <div>
              <Label>بيان (اختياري)</Label>
              <Input value={xfer.notes} onChange={(e)=>setXfer(x=>({...x, notes: e.target.value}))} placeholder="مثال: تعبئة صندوق البيع من الرئيسي" data-testid="xfer-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setXferOpen(false)}>إلغاء</Button>
            <Button onClick={doTransfer} disabled={xferBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="xfer-confirm-btn">
              {xferBusy ? "جاري التحويل..." : "تنفيذ التحويل"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
